import { createSelector } from 'reselect'
import type { OfferPageRoom, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'

import { initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'
import { arrayToObject, EmptyArray, sortBy } from 'lib/array/arrayUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { occupantsFitAnyRoomCapacityByThresholds, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import type { RoomRateViews } from 'components/OfferPage/Property/singlePropertyUtils'
import { reportLog } from 'services/userMonitoring'
import {
  getPropertyNoDatesRoomRows,
  getPropertyRatesByOccupancy,
  getPropertyRatesByRoomByOccupancy,
} from './offerPageSelectors'

type OfferPageRoomId = OfferPageRoom['id']

export type ValidRatesByRoom = Record<OfferPageRoomId, Array<App.EnrichedPropertyRate>>
export type PropertyRateByRoom = Record<OfferPageRoomId, App.EnrichedPropertyRate | undefined>

function propertyRateDisplayPrice(rate: App.EnrichedPropertyRate): number {
  return rate.luxPlusTotals?.displayPrice ?? rate.totals.displayPrice
}

function isEnrichedAvailable(
  roomRates: App.EnrichedPropertyRoomRates,
): roomRates is App.EnrichedAvailablePropertyRoomRates {
  return 'orderedHotelOnlyRates' in roomRates
}

// Per-room bookable rates via the per-occupancy seam. Reads capacities + age thresholds straight
// off `state.property.rooms` (mapped by `propertyMapper`); no derived constraint map needed.
export const getValidRatesByRoom = createSelector(
  getPropertyRatesByOccupancy,
  (state: OfferPageState) => state.property?.rooms,
  (state: OfferPageState) => state.rooms ?? initialOfferPageRooms,
  (state: OfferPageState) => state.flights,
  (ratesByOccupancy, propertyRooms, rooms, flights): ValidRatesByRoom => {
    if (!propertyRooms) return {}
    return arrayToObject(
      rooms,
      room => room.id,
      room => {
        const occKey = generateOccupancyStringByRoom(room)
        const enrichedRoomRates = ratesByOccupancy[occKey]
        if (!enrichedRoomRates?.length) return EmptyArray
        const bookable: Array<App.EnrichedPropertyRate> = []
        for (const roomRates of enrichedRoomRates) {
          if (!isEnrichedAvailable(roomRates)) continue
          // Accommodation service only returns rates for rooms that fit the requested occupancy
          // (others come back flagged unavailable and are dropped by isEnrichedAvailable above), so
          // we trust that fit here rather than re-deriving it, matching the single-room path.
          if (!propertyRooms[roomRates.roomId]) continue
          const rates = flights ? roomRates.orderedHotelWithFlightsRates : roomRates.orderedHotelOnlyRates
          bookable.push(...rates)
        }
        return bookable
      },
    )
  },
)

function inventoryKey(rate: App.EnrichedPropertyRate): string {
  return `${rate.roomId}::${rate.optionId}`
}

function hasRateAvailability(rate: App.EnrichedPropertyRate): rate is App.EnrichedPropertyRate & { availableRooms: number } {
  return typeof rate.availableRooms === 'number'
}

// Same rate may appear once per room. Entries collapse via `Math.min` (not sum): if
// occupancy-specific responses disagree, cap selection to the most constrained observed inventory.
function buildRemainingInventory(validByRoom: ValidRatesByRoom): Map<string, number> {
  const remaining = new Map<string, number>()
  for (const rates of Object.values(validByRoom)) {
    for (const rate of rates) {
      const key = inventoryKey(rate)
      const current = remaining.get(key)
      // Keep unknown inventory undefined in the display layer so we do not show a false urgency
      // count. A returned rate is still bookable, so only this booking resolver falls back to one.
      const availableRooms = rate.availableRooms ?? 1
      remaining.set(key, current === undefined ? availableRooms : Math.min(current, availableRooms))
    }
  }
  return remaining
}

function warnMissingInventory(rate: App.EnrichedPropertyRate): void {
  reportLog('SPP multi-room inventory missing for tracked rate', {
    roomId: rate.roomId,
    optionId: rate.optionId,
  }, 'warn')
}

function hasInventory(remaining: Map<string, number>, rate: App.EnrichedPropertyRate): boolean {
  const current = remaining.get(inventoryKey(rate))
  if (current === undefined) {
    warnMissingInventory(rate)
    return false
  }
  return current > 0
}

function decrementInventory(remaining: Map<string, number>, rate: App.EnrichedPropertyRate): void {
  const key = inventoryKey(rate)
  const current = remaining.get(key)
  if (current === undefined) {
    warnMissingInventory(rate)
    return
  }
  remaining.set(key, current - 1)
}

// Tie-breaks identically-priced rates by inventory key so cheapest resolves reproducibly across refetches.
function pickCheapestStable(rates: ReadonlyArray<App.EnrichedPropertyRate>): App.EnrichedPropertyRate | undefined {
  if (rates.length === 0) return undefined
  let best = rates[0]
  let bestPrice = propertyRateDisplayPrice(best)
  let bestKey = inventoryKey(best)
  for (let i = 1; i < rates.length; i++) {
    const candidate = rates[i]
    const candidatePrice = propertyRateDisplayPrice(candidate)
    if (candidatePrice > bestPrice) continue
    const candidateKey = inventoryKey(candidate)
    if (candidatePrice < bestPrice || candidateKey < bestKey) {
      best = candidate
      bestPrice = candidatePrice
      bestKey = candidateKey
    }
  }
  return best
}

// Process occupancy rooms largest-first with pre-existing selections prioritised so a room that
// needs a specific rate is not pre-empted by a smaller room with no preference.
export function resolveActivePropertyRatesByRoom(
  validByRoom: ValidRatesByRoom,
  rooms: Array<OfferPageRoom>,
): PropertyRateByRoom {
  const active: PropertyRateByRoom = {}
  if (rooms.length === 0) return active

  const roomsToProcess = sortBy(
    sortBy(rooms, room => (room.roomId && room.rateId ? 1 : 0), 'desc'),
    room => sumUpOccupancies([room]),
    'desc',
  )

  const remaining = buildRemainingInventory(validByRoom)

  roomsToProcess.forEach(room => {
    const valid = validByRoom[room.id] ?? EmptyArray

    if (room.roomId && room.rateId) {
      const match = valid.find(rate =>
        rate.roomId === room.roomId &&
        rate.optionId === room.rateId &&
        hasInventory(remaining, rate),
      )
      if (match) {
        active[room.id] = match
        decrementInventory(remaining, match)
        return
      }
    }

    if (room.roomId) {
      const inSelectedRoom = valid.filter(rate =>
        rate.roomId === room.roomId && hasInventory(remaining, rate),
      )
      const cheapestInRoom = pickCheapestStable(inSelectedRoom)
      if (cheapestInRoom) {
        active[room.id] = cheapestInRoom
        decrementInventory(remaining, cheapestInRoom)
        return
      }
    }

    const cheapest = pickCheapestStable(valid.filter(rate => hasInventory(remaining, rate)))
    if (cheapest) {
      active[room.id] = cheapest
      decrementInventory(remaining, cheapest)
    } else {
      active[room.id] = undefined
    }
  })

  return active
}

export const getActivePropertyRatesByRoom = createSelector(
  getValidRatesByRoom,
  (state: OfferPageState) => state.rooms ?? initialOfferPageRooms,
  resolveActivePropertyRatesByRoom,
)

export const getSelectedPropertyRatesByRoom = createSelector(
  getValidRatesByRoom,
  (state: OfferPageState) => state.rooms ?? initialOfferPageRooms,
  (validByRoom, rooms): PropertyRateByRoom => {
    return arrayToObject(
      rooms,
      room => room.id,
      room => {
        if (!room.roomId || !room.rateId) return undefined
        const valid = validByRoom[room.id] ?? EmptyArray
        return valid.find(rate =>
          rate.roomId === room.roomId && rate.optionId === room.rateId,
        )
      },
    )
  },
)

function maxRateAvailability(rates: ReadonlyArray<App.EnrichedPropertyRate>): number | undefined {
  return rates.reduce<number | undefined>((acc, rate) => {
    if (!hasRateAvailability(rate)) return acc
    return acc === undefined ? rate.availableRooms : Math.max(acc, rate.availableRooms)
  }, undefined)
}

// Per-occupancy room rows. Every room keeps its bookable rates; cross-room contention is resolved by
// resolveActivePropertyRatesByRoom at selection/checkout, not by hiding rates here. Before dates are
// chosen there are no dated rows, so fall back to the capacity-filtered date-independent list.
export const getPropertyIndividualRowsByRoomId = createSelector(
  getPropertyRatesByRoomByOccupancy,
  (state: OfferPageState) => state.rooms ?? initialOfferPageRooms,
  getPropertyNoDatesRoomRows,
  (rowViewsByOccupancy, rooms, noDatesRows): Record<string, Array<RoomRateViews>> => {
    const out: Record<string, Array<RoomRateViews>> = {}

    if (noDatesRows.length > 0) {
      // Before dates there are no rates to filter on, so scope each occupancy room to the room types
      // whose capacity can actually fulfil its occupancy rather than showing every room type.
      for (const room of rooms) {
        out[room.id] = noDatesRows.filter(row =>
          occupantsFitAnyRoomCapacityByThresholds(
            room,
            row.propertyRoom.capacities,
            row.propertyRoom.ageCategoryThresholds,
          ),
        )
      }
      return out
    }

    for (const room of rooms) {
      const occKey = generateOccupancyStringByRoom(room)
      // Keep every valid rate visible so the shopper can always pick a different room/rate,
      // matching legacy bedbank multi-room (which does not hide rates on inventory contention).
      out[room.id] = (rowViewsByOccupancy[occKey] ?? EmptyArray).map(row => ({
        ...row,
        availableRooms: maxRateAvailability(row.rates),
      }))
    }
    return out
  },
)
